import { NavLink } from "react-router-dom";
import { aide } from "./aide/aides";
import type { AideId } from "./aide/aides";
import { Icon } from "./icons";
import type { ModuleDef } from "./module-contract";
import { NavBadge } from "./NavBadge";

/**
 * Les 19 modules connus, un par lien de nav — la SEULE liste qui fait le pont
 * entre `mod.id` (chaîne libre côté registre, module-contract.ts) et l'aide
 * au survol. `AIDE_PAR_MODULE` étant un `Record` exhaustif sur ce littéral,
 * oublier l'aide d'un module listé ici est un ÉCHEC DE COMPILATION — jamais
 * un repli silencieux.
 *
 * Ajouter ici tout nouveau module : sans ça, son lien n'a pas d'aide et
 * Shell.aides.test.tsx échoue.
 */
const IDS_MODULES = [
  "pilotage",
  "travaux-du-jour",
  "travaux",
  "relances",
  "organisations",
  "praticiens",
  "messages",
  "emails",
  "appels",
  "courses",
  "kanban",
  "partage",
  "vevidental",
  "pennylane",
  "finance",
  "encours-ancien",
  "connaissances",
  "admin",
  "bienvenue",
] as const;
type ModuleId = (typeof IDS_MODULES)[number];

const AIDE_PAR_MODULE: Record<ModuleId, AideId> = {
  pilotage: "nav.tableau",
  "travaux-du-jour": "nav.travauxDuJour",
  travaux: "nav.travaux",
  relances: "nav.relancer",
  organisations: "nav.cabinets",
  praticiens: "nav.clients",
  messages: "nav.whatsapp",
  emails: "nav.emails",
  appels: "nav.appels",
  courses: "nav.courses",
  kanban: "nav.kanban",
  partage: "nav.partage",
  vevidental: "nav.vevidental",
  pennylane: "nav.pennylane",
  finance: "nav.finance",
  "encours-ancien": "nav.encoursAncien",
  connaissances: "nav.connaissances",
  admin: "nav.admin",
  bienvenue: "nav.bienvenue",
};

function estModuleConnu(id: string): id is ModuleId {
  return (IDS_MODULES as readonly string[]).includes(id);
}

/** Un module déjà signalé ne l'est plus : un avertissement par id, pas par rendu. */
const modulesSansAideSignales = new Set<string>();

/**
 * Repli NON fatal. Avant le 09/09, un module absent d'`IDS_MODULES` levait une
 * exception PENDANT le rendu de la nav : toute la coque devenait blanche, plus
 * une page atteignable, pour un simple oubli d'aide. Le lien sort donc SANS
 * `data-aide-id`, et l'oubli reste visible sur deux plans : un avertissement en
 * console pour la personne qui développe, et surtout l'invariant
 * `Shell.aides.test.tsx` (« 0 élément actif sans aide ») qui, lui, REFUSE en CI.
 */
export function aideDuModule(id: string): AideId | null {
  if (!estModuleConnu(id)) {
    if (!modulesSansAideSignales.has(id)) {
      modulesSansAideSignales.add(id);
      console.warn(`Module sans aide : ${id} — l'ajouter à IDS_MODULES`);
    }
    return null;
  }
  return AIDE_PAR_MODULE[id];
}

/** Le lien de nav d'UN module — le même pour le menu complet et le menu réduit. */
export function LienNav({ mod, onClick }: { mod: ModuleDef; onClick: () => void }) {
  const first = mod.routes[0];
  if (!first) return null;
  const aideId = aideDuModule(mod.id);
  return (
    <NavLink to={first.path} className={({ isActive }) => (isActive ? "on" : "")} onClick={onClick} {...(aideId ? aide(aideId) : {})}>
      <Icon id={mod.icon} />
      <span className="lbl">{mod.label}</span>
      {mod.badge && <NavBadge badge={mod.badge} />}
    </NavLink>
  );
}

/**
 * LE LIEN D'UN MODULE DANS LE PIED DE LA COQUE (C3) — même dérivation que
 * `LienNav`, même repli non fatal sur l'aide manquante.
 *
 * Icône seule : le pied est étroit (avatar + nom + e-mail + déconnexion) et
 * un libellé y déborderait. Le nom passe donc par `aria-label` — il reste lu
 * par un lecteur d'écran et il reste le nom du lien pour les tests, mais il
 * n'est écrit nulle part en dur : il vient de `mod.label`.
 */
export function LienPied({ mod }: { mod: ModuleDef }) {
  const first = mod.routes[0];
  if (!first) return null;
  const aideId = aideDuModule(mod.id);
  return (
    <NavLink className="ib" to={first.path} aria-label={mod.label} {...(aideId ? aide(aideId) : {})}>
      <Icon id={mod.icon} size="sm" />
    </NavLink>
  );
}
